import { Button, Input, SegmentedControl } from '@root/components'
import { TagSelector } from '@root/containers/TagSelector/TagSelector'
import { useBusinessContext } from '@root/context'
import { IPurchaseInvoice, invalidateDashboardQueries, updatePurchaseInvoice } from '@root/query'
import { setAPIErrors } from '@root/utils'
import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'

interface IProps {
  purchaseInvoice: IPurchaseInvoice
  onClose: () => void
}

const PurchaseInvoiceForm: React.FC<IProps> = ({
  purchaseInvoice: oldPurchaseInvoice,
  onClose: onCloseCallBack
}) => {
  const [t] = useTranslation()
  const {
    slug,
    data: { has_tags: hasTags }
  } = useBusinessContext()
  const queryClient = useQueryClient()
  const [remittanceType, setRemittanceType] = useState<'ref' | 'msg'>(
    oldPurchaseInvoice.message && !oldPurchaseInvoice.reference ? 'msg' : 'ref'
  )

  const getDefaultValuesFromPurchaseInvoice = (purchaseInvoice: IPurchaseInvoice) => {
    return {
      invoicing_date: purchaseInvoice?.invoicing_date,
      due_date: purchaseInvoice?.due_date,
      sender_bank_account: purchaseInvoice?.sender_bank_account,
      sender_bank_bic: purchaseInvoice?.sender_bank_bic,
      sender_name: purchaseInvoice?.sender_name,
      amount: purchaseInvoice?.amount,
      reference: purchaseInvoice?.reference,
      message: purchaseInvoice?.message,
      tag_ids: purchaseInvoice?.tag_ids || []
    }
  }

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<Partial<IPurchaseInvoice>>({
    defaultValues: getDefaultValuesFromPurchaseInvoice(oldPurchaseInvoice)
  })

  const updateMutation = useMutation<IPurchaseInvoice, unknown, Partial<IPurchaseInvoice>>({
    mutationFn: data =>
      updatePurchaseInvoice({ slug, purchaseInvoiceId: oldPurchaseInvoice.id }, data),
    ...{
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [slug, 'combined_invoices'] })
        await queryClient.invalidateQueries({
          queryKey: [slug, 'purchase_invoices', oldPurchaseInvoice.id]
        })
        await invalidateDashboardQueries(queryClient, slug)
        toast.success(t('general.success'))
        onCloseCallBack()
      },
      onError: error => {
        const { status, data } = (error as any) ?? {}
        if (status === 400) {
          setAPIErrors(data, setError)
        }
        toast.error(t('general.error'))
      }
    }
  })

  const onSubmit = async data => {
    const cleanedData = { ...data }
    if (remittanceType === 'ref') delete cleanedData.message
    if (remittanceType === 'msg') delete cleanedData.reference
    updateMutation.mutate(cleanedData)
  }

  return (
    <StyledContainer>
      <StyledForm id="purchase-invoice-edit-form" onSubmit={handleSubmit(onSubmit)}>
        <Input
          type="date"
          label={t('invoicing.purchaseInvoiceForm.invoicingDate.value')}
          info={t('invoicing.purchaseInvoiceForm.invoicingDate.info')}
          {...register('invoicing_date', {
            required: { value: true, message: t('validation.required') }
          })}
          errors={errors?.invoicing_date}
          required={true}
        />

        <Input
          type="date"
          label={t('invoicing.purchaseInvoiceForm.dueDate.value')}
          info={t('invoicing.purchaseInvoiceForm.dueDate.info')}
          {...register('due_date', {
            required: { value: true, message: t('validation.required') }
          })}
          errors={errors?.due_date}
          required={true}
        />

        <SectionDivider />

        <Input
          type="text"
          label={t('invoicing.purchaseInvoiceForm.senderBankAccount.value')}
          info={t('invoicing.purchaseInvoiceForm.senderBankAccount.info')}
          {...register('sender_bank_account', {
            required: { value: true, message: t('validation.required') }
          })}
          errors={errors?.sender_bank_account}
          required={true}
        />

        <Input
          type="text"
          label={t('invoicing.purchaseInvoiceForm.senderBankBic.value')}
          info={t('invoicing.purchaseInvoiceForm.senderBankBic.info')}
          {...register('sender_bank_bic', {
            required: { value: true, message: t('validation.required') }
          })}
          errors={errors?.sender_bank_bic}
          required={true}
        />

        <Input
          type="text"
          label={t('invoicing.purchaseInvoiceForm.receiverName.value')}
          info={t('invoicing.purchaseInvoiceForm.receiverName.info')}
          {...register('sender_name', {
            required: { value: true, message: t('validation.required') }
          })}
          errors={errors?.sender_name}
          required={true}
        />
        <Input
          type="number"
          label={t('invoicing.purchaseInvoiceForm.amount.value')}
          info={t('invoicing.purchaseInvoiceForm.amount.info')}
          {...register('amount', {
            valueAsNumber: true,
            required: { value: true, message: t('validation.required') }
          })}
          errors={errors?.amount}
          step={0.01}
          required={true}
        />

        <SegmentedControl
          value={remittanceType}
          onUpdate={setRemittanceType}
          options={[
            { value: 'ref', label: t('invoicing.purchaseInvoiceForm.reference.value') },
            { value: 'msg', label: t('invoicing.purchaseInvoiceForm.message.value') }
          ]}
        />

        {remittanceType === 'ref' && (
          <Input
            type="text"
            label={t('invoicing.purchaseInvoiceForm.reference.value')}
            info={t('invoicing.purchaseInvoiceForm.reference.info')}
            {...register('reference', {
              required: { value: true, message: t('validation.required') }
            })}
            errors={errors?.reference}
            required={true}
          />
        )}

        {remittanceType === 'msg' && (
          <Input
            type="text"
            label={t('invoicing.purchaseInvoiceForm.message.value')}
            info={t('invoicing.purchaseInvoiceForm.message.info')}
            {...register('message', {
              required: { value: true, message: t('validation.required') }
            })}
            errors={errors?.message}
            required={true}
          />
        )}

        {hasTags && (
          <Controller
            name="tag_ids"
            control={control}
            render={({ field }) => (
              <TagSelectorWrapper>
                <TagSelector {...field} />
              </TagSelectorWrapper>
            )}
          />
        )}
      </StyledForm>

      <FooterButtonContainer>
        <Button onClick={() => onCloseCallBack()}>{t('general.cancel')}</Button>
        <Button
          type="submit"
          intent="success"
          form="purchase-invoice-edit-form"
          showSpinner={updateMutation.isPending}
        >
          {t('general.save')}
        </Button>
      </FooterButtonContainer>
    </StyledContainer>
  )
}

export default PurchaseInvoiceForm

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`

const StyledForm = styled.form`
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 1rem;
  flex: 1;
`

const SectionDivider = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg}rem;
  width: 100%;
`

const TagSelectorWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md}rem;
`

const FooterButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding-top: ${({ theme }) => theme.spacing.lg}rem;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`
